
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GoogleFontLoader from "react-google-font-loader";
import {
  Container
} from "react-bootstrap";
import { FormComponentMode01 } from "../../components/Events01.js";
import { FormComponentMode02 } from "../../components/Events02.js";
import { EventHistory01, EventHistory02 } from './EventHistory.js'
import { EventEmails } from './EventEmails.js'
import { EventCommunicationChannels } from './EventWhatsApp.js'
import { EventLogs01, EventLogs02 } from './EventLogs.js'
import useAppDispatch from "../../hooks/useAppDispatch.js";
import useAuth from "../../hooks/useAuth.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import { EventSummaryCard } from "../../components/EventSummaryCard.js";
import { EventEditHeader } from "../../components/EventEditHeader.js";
import { EventActionLinks as AppEventActionLinks } from "../../components/EventsComponents01.js";
import { EventActionLinks as CredentialEventActionLinks } from "../../components/EventActionLinks.js";
import { hydrateFormFieldVacancies } from "../../components/EventsComponents.js";
import { domainConfig } from "../../config.js";
import { eventList, setEventView } from "../../redux/slices/events.js";
import {
  canAccessAdminEventSections,
  canAccessEventEmails,
  canAccessEventWhatsApp,
  canAccessEventRsvps,
  canManageEvent,
} from "../../utils/eventPermissions.js";
import { synchronizeRegisterTemplateVersion } from "../../utils/registrationEmailTemplates.js";

const getAuthorizedEventView = (userState, platform, viewState = {}) => {
  const canManageCurrentEvent = canManageEvent(userState, platform);
  const canViewEmails = canAccessEventEmails(userState, platform);
  const canViewWhatsApp = canAccessEventWhatsApp(userState, platform);
  const canViewRsvps = canAccessEventRsvps(userState, platform);
  const canViewLogs = canAccessAdminEventSections(userState, platform);

  if (viewState.showEmails && canViewEmails) {
    return { showHistory: false, showRecords: false, showEmails: true, showWhatsApp: false, showRsvps: false, showLogs: false };
  }

  if (viewState.showWhatsApp && canViewWhatsApp) {
    return { showHistory: false, showRecords: false, showEmails: false, showWhatsApp: true, showRsvps: false, showLogs: false };
  }

  if (viewState.showRsvps && canViewRsvps) {
    return { showHistory: false, showRecords: true, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: false };
  }

  if (viewState.showLogs && canViewLogs) {
    return { showHistory: false, showRecords: false, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: true };
  }

  if (viewState.showRecords) {
    return { showHistory: false, showRecords: true, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: false };
  }

  return {
    showHistory: viewState.showHistory || !canManageCurrentEvent,
    showRecords: false,
    showEmails: false,
    showWhatsApp: false,
    showRsvps: false,
    showLogs: false,
  };
};

const clearAnalyticsQueryParams = () => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const analyticsParameterNames = Array.from(url.searchParams.keys())
    .filter((name) => name.startsWith("analytics_"));

  if (analyticsParameterNames.length === 0) return;

  analyticsParameterNames.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

const getEventSummaryValuesKey = (values) =>
  values
    ? JSON.stringify({
        title: values.title,
        titulo: values.titulo,
        status: values.status,
        data_termino: values.data_termino,
        end_date: values.end_date,
        json: values.json,
      })
    : "";

const PersistentEventSummary = React.memo(({ platform, dataTable, values, fluid = false }) => {
  const notyf = React.useContext(NotyfContext);

  return (
    <Container fluid={fluid} className="p-0">
      <EventSummaryCard
        platform={platform}
        dataTable={dataTable}
        values={values}
        className="mb-4"
      >
        {platform === 1 && (
          <AppEventActionLinks dataTable={dataTable} notyf={notyf} />
        )}
        {platform === 2 && (
          <CredentialEventActionLinks
            dataTable={dataTable}
            notyf={notyf}
            shareUrl={domainConfig.aplicativos + "/?token=" + dataTable.token}
          />
        )}
      </EventSummaryCard>
    </Container>
  );
});

export const EventEdit = ({
  dataTable,
  fonts,
  aplicativos,
  showHistory: initialShowHistory = false,
  showRecords: initialShowRecords = false,
  showEmails: initialShowEmails = false,
  showWhatsApp: initialShowWhatsApp = false,
  showRsvps: initialShowRsvps = false,
  showLogs: initialShowLogs = false }) => {

  if (dataTable.platform === 1) {
    return (
      <EventEditMode01
        dataTable={dataTable}
        fonts={fonts}
        aplicativos={aplicativos}
        showHistory={initialShowHistory}
        showLogs={initialShowLogs}>
      </EventEditMode01>
    );
  } else if (dataTable.platform === 2) {
    return (
      <EventEditMode02
        dataTable={dataTable}
        fonts={fonts}
        showHistory={initialShowHistory}
        showRecords={initialShowRecords}
        showEmails={initialShowEmails}
        showWhatsApp={initialShowWhatsApp}
        showRsvps={initialShowRsvps}
        showLogs={initialShowLogs}>
      </EventEditMode02>
    );
  }
}

const EventEditMode01 = ({
  dataTable,
  fonts,
  aplicativos,
  showHistory: initialShowHistory = false,
  showLogs: initialShowLogs = false
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userState = useAuth();
  const canManageCurrentEvent = canManageEvent(userState, dataTable.platform);
  const canViewLogs = canAccessAdminEventSections(userState, dataTable.platform);
  const initialView = getAuthorizedEventView(userState, dataTable.platform, {
    showHistory: initialShowHistory,
    showLogs: initialShowLogs,
  });
  const [applications, setApplications] = useState(dataTable.aplicativos);
  const [dataAplicativo, setDataAplicativo] = useState(false);
  const [dataAddForm, setDataAddForm] = useState({
    addFieldData: false,
    importUsers: {
      users: [],
      fields: [],
      error: '',
      errorList: [],
      report: null,
      formValue: '',
      formFilename: '',
      disableValidation: false
    },
    fields: hydrateFormFieldVacancies(
      dataTable.json.form.fields ? dataTable.json.form.fields : [],
      dataTable.form_field_vacancies,
    ),
    fieldsToShow: dataTable.json.form.fieldsToShow ? dataTable.json.form.fieldsToShow : [],
    socialMedia: dataTable.json.layout.footer.socialMedia ? dataTable.json.layout.footer.socialMedia : []
  });
  const [modal, setModal] = useState({ showModalForm: false, showSocialMediaForm: false, showModalAplicativos: false });
  const [updateDataTable, setDataTableUpdate] = useState(false);
  const [showHistory, setShowHistory] = useState(initialView.showHistory);
  const [showLogs, setShowLogs] = useState(initialView.showLogs);
  const [summaryValues, setSummaryValues] = useState(null);

  useEffect(() => {
    if (initialShowLogs && !canViewLogs) {
      dispatch(eventList({}));
      navigate('/');
    }
  }, [canViewLogs, dispatch, initialShowLogs, navigate]);

  // Set initial history state on mount
  useEffect(() => {
    // Ensure we have a history entry for this view
    const currentState = window.history.state;
    if (currentState?.view !== 'event-edit-01') {
      window.history.pushState(
        { view: 'event-edit-01', showHistory, showLogs, eventId: dataTable.evento_id },
        ''
      );
    }
  }, [showHistory, showLogs, dataTable.evento_id]);

  // Handle browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.view === 'event-edit-01') {
        if (event.state.showLogs && !canViewLogs) {
          navigate('/');
          dispatch(eventList({}));
          return;
        }

        const nextView = getAuthorizedEventView(userState, dataTable.platform, event.state);
        setShowHistory(nextView.showHistory);
        setShowLogs(nextView.showLogs);
      } else {
        // User pressed back to go to event list (or fell through to previous route)
        // Return to the event list while preserving its applied filters.
        navigate('/');
        dispatch(eventList({}));
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [canViewLogs, dataTable.platform, dispatch, navigate, userState]);

  const handleSetShowHistory = useCallback((value) => {
    window.history.pushState(
      { view: 'event-edit-01', showHistory: value, showLogs: false, eventId: dataTable.evento_id },
      ''
    );
    setShowHistory(value);
    if (value) {
      setShowLogs(false);
    }
  }, [dataTable.evento_id]);

  const handleSetShowLogs = useCallback((value) => {
    if (value && !canViewLogs) {
      dispatch(eventList({}));
      navigate('/');
      return;
    }

    window.history.pushState(
      { view: 'event-edit-01', showHistory: false, showLogs: value, eventId: dataTable.evento_id },
      ''
    );
    setShowLogs(value);
    if (value) {
      setShowHistory(false);
    }
  }, [canViewLogs, dataTable.evento_id, dispatch, navigate]);

  const handleSummaryValuesChange = useCallback((values) => {
    const nextValuesKey = getEventSummaryValuesKey(values);

    setSummaryValues((previousValues) => {
      return previousValues && getEventSummaryValuesKey(previousValues) === nextValuesKey
        ? previousValues
        : values;
    });
  }, []);

  let eventContent;

  if (showHistory) {
    eventContent = (
      <EventHistory01
        dataTable={dataTable}
        updateDataTable={updateDataTable}
        setDataTableUpdate={setDataTableUpdate}>
      </EventHistory01>
    );
  } else if (showLogs) {
    eventContent = (
      <EventLogs01
        dataTable={dataTable}
        refreshTrigger={updateDataTable}>
      </EventLogs01>
    );
  } else {
    eventContent = (
      <React.Fragment>
        <Container className="p-0">
          <GoogleFontLoader fonts={fonts} />
          <FormComponentMode01
            fonts={fonts}
            dataTable={dataTable}
            setShowHistory={handleSetShowHistory}
            setShowLogs={handleSetShowLogs}
            aplicativos={aplicativos}
            applications={applications}
            setApplications={setApplications}
            dataAplicativo={dataAplicativo}
            setDataAplicativo={setDataAplicativo}
            dataAddForm={dataAddForm}
            setDataAddForm={setDataAddForm}
            modal={modal}
            setModal={setModal}
            setDataTableUpdate={setDataTableUpdate}
            onValuesChange={handleSummaryValuesChange}
          >
          </FormComponentMode01>
        </Container>
      </React.Fragment >
    );
  }

  return (
    <>
      <PersistentEventSummary
        platform={1}
        dataTable={dataTable}
        values={summaryValues}
        fluid={showHistory || showLogs}
      />
      <EventEditHeader
        dataTable={dataTable}
        platform={1}
        view={showHistory ? "reports" : showLogs ? "logs" : "edit"}
        canManageCurrentEvent={canManageCurrentEvent}
        canViewLogs={canViewLogs}
        onGoToEvents={() => dispatch(eventList())}
        onGoToEdit={() => {
          if (showHistory) {
            handleSetShowHistory(false);
          } else if (showLogs) {
            handleSetShowLogs(false);
          }
        }}
        onShowHistory={() => handleSetShowHistory(true)}
        onShowLogs={() => handleSetShowLogs(true)}
        onRefresh={() => {
          setDataTableUpdate((prevCount) => prevCount + 1);
          dispatch(eventList({ fetch: true, preserveCurrent: true }));
        }}
      />
      {eventContent}
    </>
  );
};

const EventEditMode02 = ({
  dataTable,
  fonts,
  showHistory: initialShowHistory = false,
  showRecords: initialShowRecords = false,
  showEmails: initialShowEmails = false,
  showWhatsApp: initialShowWhatsApp = false,
  showRsvps: initialShowRsvps = false,
  showLogs: initialShowLogs = false
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userState = useAuth();
  const canManageCurrentEvent = canManageEvent(userState, dataTable.platform);
  const canViewEmails = canAccessEventEmails(userState, dataTable.platform);
  const canViewWhatsApp = canAccessEventWhatsApp(userState, dataTable.platform);
  const canViewRsvps = canAccessEventRsvps(userState, dataTable.platform);
  const canViewLogs = canAccessAdminEventSections(userState, dataTable.platform);
  const initialView = getAuthorizedEventView(userState, dataTable.platform, {
    showHistory: initialShowHistory,
    showRecords: initialShowRecords,
    showEmails: initialShowEmails,
    showWhatsApp: initialShowWhatsApp,
    showRsvps: initialShowRsvps,
    showLogs: initialShowLogs,
  });
  const [localDataTable, setLocalDataTable] = useState(dataTable);
  const [dataAddForm, setDataAddForm] = useState({
    activeTab: 'pagina-inicial', tabs: dataTable.json.sections.length > 0 ? dataTable.json.sections : {},
    addFieldData: false,
    importUsers: {
      users: [],
      fields: [],
      error: '',
      errorList: [],
      report: null,
      formValue: '',
      formFilename: '',
      activeUserAutomatically: false,
      disableValidation: false
    },
    fields: hydrateFormFieldVacancies(
      dataTable.json.form.fields ? dataTable.json.form.fields : [],
      dataTable.form_field_vacancies,
    ),
    fieldsUsers: dataTable.json.form.fieldsUsers ? dataTable.json.form.fieldsUsers : [],
    fieldsSympla: dataTable.json.form.fieldsSympla ? dataTable.json.form.fieldsSympla : [],
    fieldsToShow: dataTable.json.form.fieldsToShow ? dataTable.json.form.fieldsToShow : [],
    socialMedia: dataTable.json.layout.footer.socialMedia ? dataTable.json.layout.footer.socialMedia : [],
    zplModels: dataTable.json.credentialAccess.zplModels ? dataTable.json.credentialAccess.zplModels : []
  });
  const [modal, setModal] = useState({ showModalForm: false, showModalFormUser: false, showSocialMediaForm: false, showZPLModelForm: false, showExtraSectionsForm: false });
  const [updateDataTable, setDataTableUpdate] = useState(false);
  const [showHistory, setShowHistory] = useState(initialView.showHistory);
  const [showRecords, setShowRecords] = useState(initialView.showRecords);
  const [showEmails, setShowEmails] = useState(initialView.showEmails);
  const [showWhatsApp, setShowWhatsApp] = useState(initialView.showWhatsApp);
  const [showLogs, setShowLogs] = useState(initialView.showLogs);
  const [summaryValues, setSummaryValues] = useState(null);

  useEffect(() => {
    setLocalDataTable(dataTable);
  }, [dataTable]);

  useEffect(() => {
    // Analytics filters belong only to Relatórios. Preserve all other query
    // parameters when another event section becomes active.
    if (!showHistory) {
      clearAnalyticsQueryParams();
    }
  }, [showHistory]);

  useEffect(() => {
    if ((initialShowRsvps && !canViewRsvps) || (initialShowEmails && !canViewEmails) || (initialShowLogs && !canViewLogs)) {
      dispatch(eventList({}));
      navigate('/');
    }
  }, [canViewEmails, canViewLogs, canViewRsvps, dispatch, initialShowEmails, initialShowLogs, initialShowRsvps, navigate]);

  const handleRegisterTemplateVersionChange = useCallback(({ templateId, templateVersion }) => {
    const normalizedTemplateVersion = Number(templateVersion);

    if (!templateId || Number.isNaN(normalizedTemplateVersion)) {
      return;
    }

    setLocalDataTable((prev) => {
      if (!prev) {
        return prev;
      }

      const result = synchronizeRegisterTemplateVersion(
        prev.json?.email?.register,
        templateId,
        normalizedTemplateVersion,
      );

      if (!result.changed) return prev;

      return {
        ...prev,
        json: {
          ...prev.json,
          email: {
            ...prev.json?.email,
            register: result.register,
          },
        },
      };
    });
  }, []);

  // Set initial history state on mount
  useEffect(() => {
    const currentState = window.history.state;
    if (currentState?.view !== 'event-edit-02') {
      window.history.pushState(
        { view: 'event-edit-02', showHistory, showRecords, showEmails, showWhatsApp, showRsvps: false, showLogs, eventId: localDataTable.evento_id },
        ''
      );
    }
  }, [showHistory, showRecords, showEmails, showWhatsApp, showLogs, localDataTable.evento_id]);

  // Handle browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.view === 'event-edit-02') {
        if ((event.state.showRsvps && !canViewRsvps) || (event.state.showEmails && !canViewEmails) || (event.state.showLogs && !canViewLogs)) {
          navigate('/');
          dispatch(eventList({}));
          return;
        }

        const nextView = getAuthorizedEventView(userState, localDataTable.platform, event.state);
        if (!nextView.showHistory) {
          clearAnalyticsQueryParams();
        }
        dispatch(setEventView(nextView));
        setShowHistory(nextView.showHistory);
        setShowRecords(nextView.showRecords);
        setShowEmails(nextView.showEmails);
        setShowWhatsApp(nextView.showWhatsApp);
        setShowLogs(nextView.showLogs);
      } else {
        // User pressed back to go to event list (or fell through to previous route)
        // Return to the event list while preserving its applied filters.
        clearAnalyticsQueryParams();
        navigate('/');
        dispatch(eventList({}));
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [canViewEmails, canViewLogs, canViewRsvps, dispatch, localDataTable.platform, navigate, userState]);

  // Wrapper to push history state when showHistory changes
  const handleSetShowHistory = useCallback((value) => {
    window.history.pushState(
      { view: 'event-edit-02', showHistory: value, showRecords: false, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: false, eventId: localDataTable.evento_id },
      ''
    );
    dispatch(setEventView({ showHistory: value }));
    setShowHistory(value);
    if (value) {
      setShowRecords(false);
      setShowEmails(false);
      setShowWhatsApp(false);
      setShowLogs(false);
    }
  }, [dispatch, localDataTable.evento_id]);

  const handleSetShowRecords = useCallback((value) => {
    window.history.pushState(
      { view: 'event-edit-02', showHistory: false, showRecords: value, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: false, eventId: localDataTable.evento_id },
      ''
    );
    dispatch(setEventView({ showRecords: value }));
    setShowRecords(value);
    if (value) {
      setShowHistory(false);
      setShowEmails(false);
      setShowWhatsApp(false);
      setShowLogs(false);
    }
  }, [dispatch, localDataTable.evento_id]);

  const handleSetShowEmails = useCallback((value) => {
    if (value && !canViewEmails) {
      dispatch(eventList({}));
      navigate('/');
      return;
    }

    window.history.pushState(
      { view: 'event-edit-02', showHistory: false, showRecords: false, showEmails: value, showWhatsApp: false, showRsvps: false, showLogs: false, eventId: localDataTable.evento_id },
      ''
    );
    dispatch(setEventView({ showEmails: value }));
    setShowEmails(value);
    if (value) {
      setShowHistory(false);
      setShowRecords(false);
      setShowWhatsApp(false);
      setShowLogs(false);
    }
  }, [canViewEmails, dispatch, localDataTable.evento_id, navigate]);

  const handleSetShowWhatsApp = useCallback((value) => {
    if (value && !canViewWhatsApp) {
      setShowWhatsApp(false);
      return;
    }

    window.history.pushState(
      { view: 'event-edit-02', showHistory: false, showRecords: false, showEmails: false, showWhatsApp: value, showRsvps: false, showLogs: false, eventId: localDataTable.evento_id },
      ''
    );
    dispatch(setEventView({ showWhatsApp: value }));
    setShowWhatsApp(value);
    if (value) {
      setShowHistory(false);
      setShowRecords(false);
      setShowEmails(false);
      setShowLogs(false);
    }
  }, [canViewWhatsApp, dispatch, localDataTable.evento_id]);

  const handleSetShowLogs = useCallback((value) => {
    if (value && !canViewLogs) {
      dispatch(eventList({}));
      navigate('/');
      return;
    }

    window.history.pushState(
      { view: 'event-edit-02', showHistory: false, showRecords: false, showEmails: false, showWhatsApp: false, showRsvps: false, showLogs: value, eventId: localDataTable.evento_id },
      ''
    );
    dispatch(setEventView({ showLogs: value }));
    setShowLogs(value);
    if (value) {
      setShowHistory(false);
      setShowRecords(false);
      setShowEmails(false);
      setShowWhatsApp(false);
    }
  }, [canViewLogs, dispatch, localDataTable.evento_id, navigate]);

  const handleSummaryValuesChange = useCallback((values) => {
    const nextValuesKey = getEventSummaryValuesKey(values);

    setSummaryValues((previousValues) => {
      return previousValues && getEventSummaryValuesKey(previousValues) === nextValuesKey
        ? previousValues
        : values;
    });
  }, []);

  const summaryDataTable = React.useMemo(() => {
    if (!summaryValues) return localDataTable;

    return {
      ...localDataTable,
      title: summaryValues.title,
      description: summaryValues.description,
      status: summaryValues.status,
      json: summaryValues.json,
    };
  }, [localDataTable, summaryValues]);

  let eventContent;

  if (showHistory || showRecords) {
    eventContent = (
      <EventHistory02
        dataTable={localDataTable}
        updateDataTable={updateDataTable}
        setDataTableUpdate={setDataTableUpdate}
        section={showRecords ? "records" : "reports"}>
      </EventHistory02>
    );
  } else if (showEmails) {
    eventContent = (
      <EventEmails
        dataTable={localDataTable}
        updateDataTable={updateDataTable}
        setDataTableUpdate={setDataTableUpdate}
        onRegisterTemplateVersionChange={handleRegisterTemplateVersionChange}>
      </EventEmails>
    );
  } else if (showWhatsApp) {
    eventContent = (
      <EventCommunicationChannels
        dataTable={localDataTable}
        updateDataTable={updateDataTable}>
      </EventCommunicationChannels>
    );
  } else if (showLogs) {
    eventContent = (
      <EventLogs02
        dataTable={localDataTable}
        setShowHistory={handleSetShowHistory}
        setShowEmails={handleSetShowEmails}
        setShowWhatsApp={handleSetShowWhatsApp}
        setShowLogs={handleSetShowLogs}
        refreshTrigger={updateDataTable}>
      </EventLogs02>
    );
  } else {
    eventContent = (
      <React.Fragment>
        <Container className="p-0">
          <GoogleFontLoader fonts={fonts} />
          <FormComponentMode02
            fonts={fonts}
            dataTable={localDataTable}
            setShowHistory={handleSetShowHistory}
            setShowEmails={handleSetShowEmails}
            setShowWhatsApp={handleSetShowWhatsApp}
            setShowLogs={handleSetShowLogs}
            dataAddForm={dataAddForm}
            setDataAddForm={setDataAddForm}
            modal={modal}
            setModal={setModal}
            onValuesChange={handleSummaryValuesChange}>
          </FormComponentMode02>
        </Container>
      </React.Fragment >
    );
  }

  return (
    <>
      <PersistentEventSummary
        platform={2}
        dataTable={summaryDataTable}
        values={summaryValues}
        fluid={showHistory || showRecords || showEmails || showWhatsApp || showLogs}
      />
      <EventEditHeader
        dataTable={localDataTable}
        platform={2}
        view={showHistory ? "reports" : showRecords ? "records" : showEmails ? "emails" : showWhatsApp ? "communications" : showLogs ? "logs" : "edit"}
        canManageCurrentEvent={canManageCurrentEvent}
        canViewEmails={canViewEmails}
        canViewCommunicationChannels={canViewWhatsApp}
        canViewLogs={canViewLogs}
        onGoToEvents={() => {
          clearAnalyticsQueryParams();
          dispatch(eventList());
        }}
        onGoToEdit={() => {
          if (showHistory) {
            handleSetShowHistory(false);
          } else if (showRecords) {
            handleSetShowRecords(false);
          } else if (showEmails) {
            handleSetShowEmails(false);
          } else if (showWhatsApp) {
            handleSetShowWhatsApp(false);
          } else if (showLogs) {
            handleSetShowLogs(false);
          }
        }}
        onShowHistory={() => handleSetShowHistory(true)}
        onShowRecords={() => handleSetShowRecords(true)}
        onShowEmails={() => handleSetShowEmails(true)}
        onShowCommunicationChannels={() => handleSetShowWhatsApp(true)}
        onShowLogs={() => handleSetShowLogs(true)}
        onRefresh={() => {
          setDataTableUpdate((prevCount) => prevCount + 1);
          dispatch(eventList({ fetch: true, preserveCurrent: true }));
        }}
      />
      {eventContent}
    </>
  );
};
